import { useContext, useEffect, useState, useCallback, useRef } from "react";

import ThemeContext from "../../lib/theme-context";
import { fetchEconomicCalendar } from "../../services/api/economicCalendarRequest";
import useWindowDimensions from "../../utils/width";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import EventNoteIcon from "@mui/icons-material/EventNote";
import RefreshIcon from "@mui/icons-material/Refresh";

const getImpactColor = (impact) => {
  switch (impact?.toLowerCase()) {
    case "high": return "#C62828";
    case "medium": return "#F57C00";
    case "low": return "#1565C0";
    default: return "#757575";
  }
};

const CURRENCIES = ["USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "NZD"];

const getCurrencyFlag = (currency) => {
  const flags = {
    USD: "🇺🇸",
    EUR: "🇪🇺",
    GBP: "🇬🇧",
    JPY: "🇯🇵",
    AUD: "🇦🇺",
    CAD: "🇨🇦",
    CHF: "🇨🇭",
    NZD: "🇳🇿",
    CNY: "🇨🇳",
  };
  return flags[currency] || "🏳️";
};

const EconomicCalendar = () => {
  const theme = useContext(ThemeContext);
  const { width } = useWindowDimensions();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period] = useState("week");
  const [currencyFilter] = useState(CURRENCIES);
  const [impactFilter] = useState(["High"]);
  const [timezone] = useState(() => localStorage.getItem("economic-calendar-timezone") || "local");
  const fetchIdRef = useRef(0);

  const loadData = useCallback(async () => {
    const currentId = ++fetchIdRef.current;
    setLoading(true);
    setEvents([]);

    const params = {
      period,
      ...(currencyFilter.length === 1 && { currency: currencyFilter[0] }),
      ...(impactFilter.length === 1 && { impact: impactFilter[0] }),
    };

    const result = await fetchEconomicCalendar(params);

    // Ignore stale responses from out-of-order requests
    if (currentId !== fetchIdRef.current) return;

    if (result && Array.isArray(result.events)) {
      let filtered = result.events;

      // Client-side multi-select filtering
      if (currencyFilter.length > 0) {
        filtered = filtered.filter((e) =>
          currencyFilter.includes(e.currency)
        );
      }
      if (impactFilter.length > 0) {
        filtered = filtered.filter((e) =>
          impactFilter.map((i) => i.toLowerCase()).includes(e.impact?.toLowerCase())
        );
      }

      // Sort by date ascending
      filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
      setEvents(filtered);
    } else {
      setEvents([]);
    }
    setLoading(false);
  }, [period, currencyFilter, impactFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getTz = () => (timezone === "local" ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC" : timezone);

  const getEventDateInTz = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr.replace(" ", "T"));
    if (isNaN(d.getTime())) return "";
    const tz = getTz();
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);
    const getPart = (type) => parts.find((p) => p.type === type)?.value;
    return `${getPart("year")}-${getPart("month")}-${getPart("day")}`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr.replace(" ", "T"));
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-US", {
      timeZone: getTz(),
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const formatDayHeader = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr.replace(" ", "T"));
    if (isNaN(d.getTime())) return "";
    const tz = getTz();
    const weekday = d.toLocaleDateString("en-US", { timeZone: tz, weekday: "short" });
    const month = d.toLocaleDateString("en-US", { timeZone: tz, month: "short" });
    const day = Number(d.toLocaleDateString("en-US", { timeZone: tz, day: "numeric" }));
    return { weekday, month, day };
  };

  const groupEventsByDay = (events) => {
    const groups = {};
    events.forEach((event) => {
      if (!event.date) return;
      const key = getEventDateInTz(event.date);
      if (!key) return;
      if (!groups[key]) groups[key] = [];
      groups[key].push(event);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const eventDate = getEventDateInTz(dateStr);
    const tz = getTz();
    const nowParts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const getPart = (type) => nowParts.find((p) => p.type === type)?.value;
    const todayStr = `${getPart("year")}-${getPart("month")}-${getPart("day")}`;
    return eventDate === todayStr;
  };

  const isPast = (dateStr) => {
    if (!dateStr) return false;
    const eventDate = getEventDateInTz(dateStr);
    const tz = getTz();
    const nowParts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const getPart = (type) => nowParts.find((p) => p.type === type)?.value;
    const todayStr = `${getPart("year")}-${getPart("month")}-${getPart("day")}`;
    return eventDate < todayStr;
  };

  const getEventProximity = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr.replace(" ", "T"));
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    const diffMs = d - now;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    if (diffMs < 0) return 'past';
    if (diffHours <= 1) return 'urgent';
    if (diffHours <= 3) return 'soon';
    if (diffHours <= 24) return 'today';
    return null;
  };

  const getProximityColor = (proximity) => {
    if (proximity === 'urgent') return theme[0] === 'dark' ? 'rgba(220, 38, 38, 0.15)' : 'rgba(220, 38, 38, 0.1)';
    if (proximity === 'soon') return theme[0] === 'dark' ? 'rgba(234, 179, 8, 0.15)' : 'rgba(234, 179, 8, 0.1)';
    if (proximity === 'today') return theme[0] === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)';
    return null;
  };

  const getProximityBorderColor = (proximity) => {
    if (proximity === 'urgent') return '#dc2626';
    if (proximity === 'soon') return '#eab308';
    if (proximity === 'today') return '#3b82f6';
    return null;
  };

  const isMobile = width < 700;
  const bgMain = theme[0] === "dark" ? "#1a1b1e" : "#f9fafb";
  const textMain = theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e";
  const cardBg = theme[0] === "dark" ? "#25262b" : "#ffffff";
  const borderColor = theme[0] === "dark" ? "#2d2e33" : "#e5e7eb";
  const mutedText = theme[0] === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)";
  const dimText = theme[0] === "dark" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)";

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: bgMain,
        color: textMain,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "1600px",
          mx: "auto",
          minHeight: "calc(100vh - 64px)",
          px: { xs: "16px", sm: "20px" },
          py: { xs: "12px", sm: "20px" },
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
  

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: isMobile ? "center" : "flex-start",
          justifyContent: isMobile ? "center" : "space-between",
          gap: "12px",
        }}
      >

        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: isMobile ? "8px" : "12px", marginLeft: "auto" }}>
          <Tooltip title="Refresh calendar data">
            <IconButton
              onClick={loadData}
              disabled={loading}
              sx={{
                color: textMain,
                backgroundColor: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: "8px",
                padding: "8px",
                "&:hover": {
                  backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#f3f4f6",
                },
                "&:disabled": {
                  opacity: 0.5,
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: "1.2rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={80}
              sx={{
                borderRadius: "12px",
                backgroundColor:
                  theme[0] === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
              }}
            />
          ))}
        </Box>
      ) : events.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              px: 4,
              py: 2,
              borderRadius: "16px",
              backgroundColor: cardBg,
              border: `1px solid ${borderColor}`,
              boxShadow:
                theme[0] === "dark"
                  ? "0 2px 8px rgba(0,0,0,0.3)"
                  : "0 2px 8px rgba(0,0,0,0.06)",
              width: "100%",
            }}
          >
            <EventNoteIcon
              sx={{
                fontSize: "2.5rem",
                color:
                  theme[0] === "dark"
                    ? "rgba(255,255,255,0.3)"
                    : "rgba(0,0,0,0.3)",
              }}
            />
            <Typography
              sx={{
                color:
                  theme[0] === "dark"
                    ? "rgba(255,255,255,0.7)"
                    : "rgba(0,0,0,0.6)",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              {period === "today"
                ? "No events today"
                : "No events found for the selected filters."}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {/* Column Header */}
          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                borderRadius: "8px 8px 0 0",
                backgroundColor: theme[0] === "dark" ? "#25262b" : "#f3f4f6",
                border: `1px solid ${borderColor}`,
                mb: 1,
                overflow: "hidden",
              }}
            >
              {/* Spacer matching day sidebar */}
              <Box
                sx={{
                  minWidth: "80px",
                  py: 0.75,
                  borderRight: `1px solid ${borderColor}`,
                }}
              />
              {/* Titles */}
              <Box
                sx={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: isMobile ? "36px 64px 1fr" : "52px 70px 48px 1fr 100px 100px",
                  columnGap: isMobile ? "6px" : "12px",
                  alignItems: "center",
                  px: isMobile ? 1 : 2,
                  py: 0.75,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: mutedText,
                  }}
                >
                  TIME
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: mutedText,
                  }}
                >
                  CURRENCY
                </Typography>
                {!isMobile && (
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: mutedText,
                    }}
                  >
                    IMPACT
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: mutedText,
                  }}
                >
                  EVENT
                </Typography>
                {!isMobile && (
                  <>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: mutedText,
                      }}
                    >
                      FORECAST
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: mutedText,
                      }}
                    >
                      PREVIOUS
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}
          {groupEventsByDay(events)
            .sort(([dayKeyA, dayEventsA], [dayKeyB, dayEventsB]) => {
              const isPastA = isPast(dayEventsA[0]?.date);
              const isPastB = isPast(dayEventsB[0]?.date);
              if (isPastA && !isPastB) return 1;
              if (!isPastA && isPastB) return -1;
              return dayKeyA.localeCompare(dayKeyB);
            })
            .map(([dayKey, dayEvents]) => {
            const header = formatDayHeader(dayEvents[0]?.date);
            const todayFlag = isToday(dayEvents[0]?.date);
            const pastFlag = isPast(dayEvents[0]?.date);
            return (
              <Box
                key={dayKey}
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  borderRadius: "12px",
                  overflow: "hidden",
                  backgroundColor: cardBg,
                  border: `1px solid ${borderColor}`,
                  mb: 2,
                  opacity: pastFlag ? 0.5 : 1,
                }}
              >
                {/* Day sidebar */}
                <Box
                  sx={{
                    minWidth: isMobile ? "auto" : "80px",
                    p: isMobile ? "8px 12px" : "16px 8px",
                    backgroundColor: todayFlag
                      ? (theme[0] === "dark" ? "#1565C0" : "#e3f2fd")
                      : (theme[0] === "dark" ? "#25262b" : "#f3f4f6"),
                    borderRight: isMobile ? "none" : `1px solid ${borderColor}`,
                    borderBottom: isMobile ? `1px solid ${borderColor}` : "none",
                    display: "flex",
                    flexDirection: isMobile ? "row" : "column",
                    alignItems: "center",
                    justifyContent: isMobile ? "flex-start" : "center",
                    gap: isMobile ? "8px" : "4px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: isMobile ? "0.8rem" : "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: todayFlag ? "#ffffff" : textMain,
                    }}
                  >
                    {header.weekday}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: isMobile ? "0.8rem" : "0.75rem",
                      fontWeight: 500,
                      color: todayFlag ? "rgba(255,255,255,0.8)" : mutedText,
                    }}
                  >
                    {header.month} {header.day}
                  </Typography>
                </Box>

                {/* Events list */}
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  {dayEvents.map((event, idx) => {
                    const proximity = getEventProximity(event.date);
                    const proximityBg = getProximityColor(proximity);
                    const proximityBorder = getProximityBorderColor(proximity);
                    const isEventPast = proximity === 'past';
                    
                    return (
                    <Box
                      key={`${event.name}-${idx}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "36px 64px 1fr" : "52px 70px 48px 1fr 100px 100px",
                        columnGap: isMobile ? "6px" : "12px",
                        alignItems: "center",
                        px: isMobile ? 1 : 2,
                        py: 1.25,
                        borderBottom:
                          idx < dayEvents.length - 1
                            ? `1px solid ${borderColor}`
                            : "none",
                        backgroundColor: proximityBg || (
                          idx % 2 === 0
                            ? "transparent"
                            : theme[0] === "dark"
                              ? "rgba(255,255,255,0.02)"
                              : "rgba(0,0,0,0.01)"
                        ),
                        borderLeft: proximityBorder ? `3px solid ${proximityBorder}` : "none",
                        opacity: isEventPast ? 0.4 : 1,
                        "&:hover": {
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(255,255,255,0.04)"
                              : "rgba(0,0,0,0.03)",
                        },
                      }}
                    >
                      {/* Time */}
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: proximity ? 700 : 500,
                          fontVariantNumeric: "tabular-nums",
                          color: proximity === 'urgent' ? '#dc2626' : 
                                 proximity === 'soon' ? '#eab308' :
                                 proximity === 'today' ? '#3b82f6' : mutedText,
                        }}
                      >
                        {formatTime(event.date)}
                      </Typography>

                      {/* Currency */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <span style={{ fontSize: "1.1rem", lineHeight: 1 }}>
                          {getCurrencyFlag(event.currency)}
                        </span>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            color: textMain,
                          }}
                        >
                          {event.currency}
                        </Typography>
                      </Box>

                      {!isMobile && (
                        /* Impact bars */
                        <Box sx={{ display: "flex", gap: "3px" }}>
                          {[1, 2, 3].map((bar) => {
                            const level =
                              event.impact?.toLowerCase() === "high"
                                ? 3
                                : event.impact?.toLowerCase() === "medium"
                                  ? 2
                                  : event.impact?.toLowerCase() === "low"
                                    ? 1
                                    : 0;
                            return (
                              <Box
                                key={bar}
                                sx={{
                                  width: "4px",
                                  height: "12px",
                                  borderRadius: "1px",
                                  backgroundColor:
                                    bar <= level
                                      ? getImpactColor(event.impact)
                                      : theme[0] === "dark"
                                        ? "#3d3e44"
                                        : "#e5e7eb",
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}

                      {/* Event Name */}
                      <Typography
                        sx={{
                          fontSize: isMobile ? "0.75rem" : "0.85rem",
                          fontWeight: 500,
                          color: textMain,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: isMobile ? "normal" : "nowrap",
                          lineHeight: isMobile ? 1.3 : 1.5,
                        }}
                      >
                        {event.name}
                      </Typography>

                      {!isMobile && (
                        /* Forecast */
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 500,
                            color: mutedText,
                          }}
                        >
                          {event.forecast || ""}
                        </Typography>
                      )}

                      {!isMobile && (
                        /* Previous */
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 500,
                            color: dimText,
                          }}
                        >
                          {event.previous || ""}
                        </Typography>
                      )}
                    </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      </Box>
    </Box>
  );
};

export default EconomicCalendar;
